import { ReactElement, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import { isNil } from 'lodash'
import { useFeature } from '@growthbook/growthbook-react'

import { useCurrentUser, useGetOneTimePasswordQuery } from 'store/api/yay/user'
import { modalsV2 } from 'store/slices/modals'

type ModalHook = {
  isActive: boolean
  open: (data?: any) => void
  onClose: (keepData?: boolean) => void
}

const withOTPVerification = <T extends object>(
  WrappedComponent: any,
  useModalHook: () => ModalHook
) => {
  return function WithOTPVerificationComponent(props: T): ReactElement | null {
    const dispatch = useDispatch()
    const isOTPDisabled = useFeature('yay-17713')?.off

    const { data: currentUser } = useCurrentUser()
    const { data: requiresOTP } = useGetOneTimePasswordQuery(
      !isOTPDisabled && currentUser && !isNil(currentUser.email)
        ? undefined
        : skipToken
    )

    const [__isVerified, __isVerifiedSet] = useState(false)

    const modalHook = useModalHook() // this assumes the wrapper component modal is using the legacy modals from useModals (NOT modalsV2)

    const onVerify = async () => {
      __isVerifiedSet(true)
      dispatch(modalsV2.actions.closeModal({ id: 'OTPModal' }))

      modalHook.open()
    }

    useEffect(() => {
      if (modalHook.isActive && requiresOTP && !__isVerified) {
        modalHook.onClose()
        dispatch(
          modalsV2.actions.openModal({
            id: 'OTPModal',
            data: {
              onVerify,
            },
          })
        )
      }
    }, [modalHook.isActive, requiresOTP, __isVerified])

    if (!modalHook.isActive || (requiresOTP && !__isVerified)) return null

    return <WrappedComponent {...props} />
  }
}

export default withOTPVerification
